import React from "react";
import { withTranslation } from "react-i18next";
import { Grid, InputAdornment, Typography } from "@mui/material";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import YesNoRadioButton from "Components/RegisterBuildingForm/YesNoRadioButton";
import { transformerTypeEnum, transformerLocationEnum } from "common/enums";
import SelectField from "Components/Common/SelectField";

import { onlyNumberKeys, resetFieldValue } from "utils";

const UtilityAndMechanicalSystem = ({ formik, t }) => {
  const {
    setFieldValue,
    values: {
      utilityMechanicalSystem: {
        numberOfPassengerLift,
        numberOfFireLift,
        electricalSystem: {
          isTransformerForBuildingBlock,
          isStandbyPowerAutomatic,
        },
      },
    },
  } = formik;

  return (
    <Grid container columnSpacing={1} rowSpacing={1}>
      <Grid item xs={12}>
        <Typography className="mb-3 fw-bold" lineHeight={1} variant="h6">
          {t("Utility & Mechanical System")}
        </Typography>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Are there any service ducts in this building block?"
          keyName="utilityMechanicalSystem.isServiceDuct"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Have internal service ducts and shafts been properly enclosed by fire resistant walls and doors, and fire stopped at all floor levels?"
          keyName="utilityMechanicalSystem.isServiceDuctsEnclosed"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid item xs={12}>
        <Typography>
          {t(
            "How many lifts have been installed in the building, and how many are designated fire lifts?"
          )}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={3}>
        <InputFieldText
          label={t("No of Passenger Lifts") + "*"}
          name="utilityMechanicalSystem.numberOfPassengerLift"
          formik={formik}
          type="number"
          placeholder="Enter No of Passenger Lifts"
          InputProps={{
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={3}>
        <InputFieldText
          label={t("No of Fire Lifts") + "*"}
          name="utilityMechanicalSystem.numberOfFireLift"
          formik={formik}
          type="number"
          placeholder="Enter No of Fire Lifts"
          InputProps={{
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={3}>
        <InputFieldText
          label={t("Total") + "*"}
          YesNoRadioButton
          type="number"
          disabled
          value={numberOfFireLift + numberOfPassengerLift || 0}
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1, pt: 1 }}>
        <YesNoRadioButton
          label="Is automatic rescue device provided for lifts?"
          keyName="utilityMechanicalSystem.isAutomaticRescueDeviceForLift"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1, pt: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t(
              "Whether natural ventilation or mechanical ventilation is provided for building?"
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} className="d-flex gap-2 flex-wrap">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Ventilation Type") + "*"}
              value="Natural"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("No. of Natural Ventilation") + "*"}
              name="utilityMechanicalSystem.numberOfNaturalVentilation"
              formik={formik}
              type="number"
              placeholder="Enter no of natural ventilation"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} className="d-flex gap-2 flex-wrap">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Ventilation Type") + "*"}
              value="Mechanical"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("No. of Mechanical Ventilation") + "*"}
              name="utilityMechanicalSystem.numberOfMechanicalVentilation"
              formik={formik}
              type="number"
              placeholder="Enter no of mechanical ventilation"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t(
              "Are any other building systems such as boiler room, gas supply, refuse chutes provided for the building?"
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Other Building System")}
            name="utilityMechanicalSystem.otherBuildingSystem"
            formik={formik}
            placeholder="Enter Other Building System"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Typography className="mb-3 fw-bold" lineHeight={1} variant="h6">
          {t("Electrical System")}
        </Typography>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Electric Substation"
          keyName="utilityMechanicalSystem.electricalSystem.isElectricSubstation"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Transformer for this building block"
          keyName="utilityMechanicalSystem.electricalSystem.isTransformerForBuildingBlock"
          formik={formik}
          onChange={(e) => {
            resetFieldValue(setFieldValue, [
              "utilityMechanicalSystem.electricalSystem.numberOfTransformers",
              "utilityMechanicalSystem.electricalSystem.transformerType",
              "utilityMechanicalSystem.electricalSystem.transformerCapacity",
              "utilityMechanicalSystem.electricalSystem.transformerLocation",
              "utilityMechanicalSystem.electricalSystem.typeOfFireProtectionSystemForTransformer",
            ]);
            setFieldValue(
              "utilityMechanicalSystem.electricalSystem.isTransformerForBuildingBlock",
              !!+e.target.value
            );
          }}
          isRequired
        />
      </Grid>
      {isTransformerForBuildingBlock && (
        <>
          <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputFieldText
                label={t("Number of Transformers") + "*"}
                name="utilityMechanicalSystem.electricalSystem.numberOfTransformers"
                formik={formik}
                type="number"
                placeholder="Enter Number of Transformers"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                label={t("Type of transformers") + "*"}
                name="utilityMechanicalSystem.electricalSystem.transformerType"
                formik={formik}
                options={transformerTypeEnum}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputFieldText
                label={t("Transformer Capacity") + "*"}
                name="utilityMechanicalSystem.electricalSystem.transformerCapacity"
                formik={formik}
                type="number"
                placeholder="Enter Transformer Capacity"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">KVA</InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                label={t("Location of Transformer") + "*"}
                name="utilityMechanicalSystem.electricalSystem.transformerLocation"
                formik={formik}
                options={transformerLocationEnum}
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
            <Grid item>
              <Typography sx={{ pt: 1 }}>
                {`${t(
                  "Type of fire protection systems provided for transformers"
                )} (${t("if any")})`}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={4} lg={3}>
              <InputFieldText
                label={t("Type of Fire Protection System")}
                name="utilityMechanicalSystem.electricalSystem.typeOfFireProtectionSystemForTransformer"
                formik={formik}
                placeholder="Enter Type of Fire Protection System"
              />
            </Grid>
          </Grid>
        </>
      )}
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is there any substation also in this building block?"
          keyName="utilityMechanicalSystem.electricalSystem.isSubstation"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is there any type of stand by power supply or generator etc provided for the building block?"
          keyName="utilityMechanicalSystem.electricalSystem.isStandbyPower"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={2}
        sx={{ pl: 1, minHeight: "70px" }}
      >
        <YesNoRadioButton
          label="Is generator or power supply automatic in action?"
          keyName="utilityMechanicalSystem.electricalSystem.isStandbyPowerAutomatic"
          onChange={(e) => {
            setFieldValue(
              "utilityMechanicalSystem.electricalSystem.standbyPowerLocation",
              ""
            );
            setFieldValue(
              "utilityMechanicalSystem.electricalSystem.isStandbyPowerAutomatic",
              !!+e.target.value
            );
          }}
          formik={formik}
          isRequired
        />
        {isStandbyPowerAutomatic && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Power or Generator Location") + "*"}
              name="utilityMechanicalSystem.electricalSystem.standbyPowerLocation"
              formik={formik}
              options={transformerLocationEnum}
            />
          </Grid>
        )}
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Are use of correct milli amps RCCB/ELCB?"
          keyName="utilityMechanicalSystem.electricalSystem.isUseOfRCCBELCB"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Whether body earthing is provided in all the panels and hinges?"
          keyName="utilityMechanicalSystem.electricalSystem.isEarthing"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Has there been a lightening protection system provided as per latest NBC guideline"
          keyName="utilityMechanicalSystem.electricalSystem.islighteningProtectionSystem"
          formik={formik}
          isRequired
        />
      </Grid>
    </Grid>
  );
};

UtilityAndMechanicalSystem.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(UtilityAndMechanicalSystem);
